'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Award {
  year: string;
  title: string;
  issuer: string;
  description: string;
  category: 'safety' | 'innovation' | 'service' | 'sustainability';
}

interface IndustryRecognitionProps {
  className?: string;
}

const IndustryRecognition: React.FC<IndustryRecognitionProps> = ({ className = '' }) => {
  const [activeTab, setActiveTab] = useState<'awards' | 'partnerships'>('awards');

  const awards: Award[] = [
    {
      year: '2024',
      title: 'Award Name 1',
      issuer: 'Issuing Body 1',
      description: 'Brief recognition summary for award name 1.',
      category: 'safety',
    },
  ];

  const partnerships = [
    {
      name: 'International Association of Drilling Contractors (IADC)',
      role: 'Active Member',
      description: 'Contributing to industry standards development and best practice sharing',
      logo: '/assets/images/no_image.png',
      alt: 'IADC logo on industrial blue background',
    },
    {
      name: 'European Offshore Safety Association',
      role: 'Technical Committee Member',
      description: 'Participating in safety protocol development and regulatory guidance',
      logo: '/assets/images/no_image.png',
      alt: 'Safety association emblem with shield design',
    },
    {
      name: 'API Certification Program',
      role: 'Certified Service Provider',
      description: 'Maintaining highest standards in quality and technical competence',
      logo: '/assets/images/no_image.png',
      alt: 'API certification badge on professional background',
    },
    {
      name: 'Norwegian Petroleum Safety Authority',
      role: 'Regulatory Compliance Partner',
      description: 'Collaborating on safety standards and regulatory compliance initiatives',
      logo: '/assets/images/no_image.png',
      alt: 'Norwegian regulatory authority logo',
    },
    {
      name: 'North Sea Operators Forum',
      role: 'Strategic Partner',
      description: 'Supporting operational excellence and industry collaboration',
      logo: '/assets/images/no_image.png',
      alt: 'North Sea operators forum emblem',
    },
    {
      name: 'European Energy Association',
      role: 'Sustainability Advocate',
      description: 'Promoting environmental responsibility and sustainable practices',
      logo: '/assets/images/no_image.png',
      alt: 'European energy association logo with green elements',
    },
  ];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'safety':
        return 'ShieldCheckIcon';
      case 'innovation':
        return 'LightBulbIcon';
      case 'service':
        return 'StarIcon';
      case 'sustainability':
        return 'GlobeAltIcon';
      default:
        return 'TrophyIcon';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'safety':
        return 'bg-success/10 text-success';
      case 'innovation':
        return 'bg-primary/10 text-primary';
      case 'service':
        return 'bg-accent/10 text-accent';
      case 'sustainability':
        return 'bg-secondary/10 text-secondary';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  return (
    <section data-component-id="src/app/about/components/IndustryRecognition.tsx:108:4" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="108" data-component-end-line="248" data-component-file="IndustryRecognition.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D"
      className={`py-20 lg:py-28 bg-surface ${className}`}
      data-anim="fade-up"
      data-anim-trigger="scroll"
      data-anim-id="about-recognition-section"
    >
      <div data-component-id="src/app/about/components/IndustryRecognition.tsx:114:6" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="114" data-component-end-line="247" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
        <div data-component-id="src/app/about/components/IndustryRecognition.tsx:115:8" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="115" data-component-end-line="134" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D"
          className="text-center mb-12"
          data-anim="fade-up"
          data-anim-trigger="scroll"
          data-anim-delay="40"
          data-anim-id="about-recognition-head"
        >
          <div data-component-id="src/app/about/components/IndustryRecognition.tsx:122:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="122" data-component-end-line="124" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-block%20bg-primary%2F10%20text-primary%20px-4%20py-2%20rounded-full%20text-sm%20font-semibold%20mb-6%22%2C%22textContent%22%3A%22Industry%20Recognition%22%7D" className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Industry Recognition
          </div>

          <h2 data-component-id="src/app/about/components/IndustryRecognition.tsx:126:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="126" data-component-end-line="128" data-component-file="IndustryRecognition.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-5xl%20font-bold%20text-text-primary%20mb-6%22%2C%22textContent%22%3A%22Trusted%20by%20Industry%20Leaders%22%7D" className="text-3xl lg:text-5xl font-bold text-text-primary mb-6">
            Trusted by Industry Leaders
          </h2>

          <p data-component-id="src/app/about/components/IndustryRecognition.tsx:130:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="130" data-component-end-line="133" data-component-file="IndustryRecognition.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-text-secondary%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Our%20commitment%20to%20excellence%20has%20earned%20recognition%20from%20leading%20industry%20organizations%5Cn%20%20%20%20%20%20%20%20%20%20%20%20and%20regulatory%20bodies%22%7D" className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our commitment to excellence has earned recognition from leading industry organizations
            and regulatory bodies
          </p>
        </div>

        <div data-component-id="src/app/about/components/IndustryRecognition.tsx:136:8" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="136" data-component-end-line="165" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20mb-8%22%7D"
          className="flex justify-center mb-8"
          data-anim="fade"
          data-anim-trigger="scroll"
          data-anim-delay="80"
          data-anim-id="about-recognition-tabs"
        >
          <div data-component-id="src/app/about/components/IndustryRecognition.tsx:143:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="143" data-component-end-line="164" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20bg-white%20rounded-lg%20p-1%20shadow-md%22%7D" className="inline-flex bg-white rounded-lg p-1 shadow-md">
            <button data-component-id="src/app/about/components/IndustryRecognition.tsx:144:12" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="144" data-component-end-line="153" data-component-file="IndustryRecognition.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Awards%20%26%20Recognition%22%7D"
              onClick={() => setActiveTab('awards')}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                activeTab === 'awards'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Awards & Recognition
            </button>
            <button data-component-id="src/app/about/components/IndustryRecognition.tsx:154:12" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="154" data-component-end-line="163" data-component-file="IndustryRecognition.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Industry%20Partnerships%22%7D"
              onClick={() => setActiveTab('partnerships')}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                activeTab === 'partnerships'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Industry Partnerships
            </button>
          </div>
        </div>

        {activeTab === 'awards' && (
          <div data-component-id="src/app/about/components/IndustryRecognition.tsx:168:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="168" data-component-end-line="203" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-anim="fade"
            data-anim-trigger="scroll"
            data-anim-delay="60"
            data-anim-id="about-recognition-awards"
          >
            {awards.map((award, index) => (
              <div data-component-id="src/app/about/components/IndustryRecognition.tsx:176:14" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="176" data-component-end-line="201" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-xl%20p-6%20shadow-lg%20hover%3Ashadow-xl%20transition-all%20duration-300%20group%22%7D"
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
                data-anim="fade-up"
                data-anim-trigger="scroll"
                data-anim-delay={80 + index * 40}
                data-anim-duration="220"
                data-anim-id={`about-recognition-award-${index}`}
              >
                <div data-component-id="src/app/about/components/IndustryRecognition.tsx:185:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="185" data-component-end-line="194" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20mb-4%22%7D" className="flex items-start justify-between mb-4">
                  <div data-component-id="src/app/about/components/IndustryRecognition.tsx:186:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="186" data-component-end-line="190" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryColor(award.category)} group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon data-component-id="src/app/about/components/IndustryRecognition.tsx:189:20" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="189" data-component-end-line="189" data-component-file="IndustryRecognition.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={getCategoryIcon(award.category) as any} size={24} />
                  </div>
                  <span data-component-id="src/app/about/components/IndustryRecognition.tsx:191:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="191" data-component-end-line="193" data-component-file="IndustryRecognition.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%2F10%20text-primary%20text-sm%20font-bold%20rounded-full%22%7D" className="px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full">
                    {award.year}
                  </span>
                </div>

                <h3 data-component-id="src/app/about/components/IndustryRecognition.tsx:196:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="196" data-component-end-line="196" data-component-file="IndustryRecognition.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-bold%20text-text-primary%20mb-2%22%7D" className="text-xl font-bold text-text-primary mb-2">{award.title}</h3>

                <p data-component-id="src/app/about/components/IndustryRecognition.tsx:198:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="198" data-component-end-line="198" data-component-file="IndustryRecognition.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-primary%20font-semibold%20mb-3%22%7D" className="text-sm text-primary font-semibold mb-3">{award.issuer}</p>

                <p data-component-id="src/app/about/components/IndustryRecognition.tsx:200:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="200" data-component-end-line="200" data-component-file="IndustryRecognition.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-sm%20leading-relaxed%22%7D" className="text-text-secondary text-sm leading-relaxed">{award.description}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'partnerships' && (
          <div data-component-id="src/app/about/components/IndustryRecognition.tsx:207:10" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="207" data-component-end-line="245" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-anim="fade"
            data-anim-trigger="scroll"
            data-anim-delay="60"
            data-anim-id="about-recognition-partners"
          >
            {partnerships.map((partner, index) => (
              <div data-component-id="src/app/about/components/IndustryRecognition.tsx:215:14" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="215" data-component-end-line="243" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-xl%20overflow-hidden%20shadow-lg%20hover%3Ashadow-xl%20transition-all%20duration-300%22%7D"
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                data-anim="fade-up"
                data-anim-trigger="scroll"
                data-anim-delay={80 + index * 40}
                data-anim-duration="220"
                data-anim-id={`about-recognition-partner-${index}`}
              >
                <div data-component-id="src/app/about/components/IndustryRecognition.tsx:224:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="224" data-component-end-line="230" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-surface%20flex%20items-center%20justify-center%20p-4%22%7D" className="h-32 bg-surface flex items-center justify-center p-4">
                  <AppImage data-component-id="src/app/about/components/IndustryRecognition.tsx:225:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="225" data-component-end-line="229" data-component-file="IndustryRecognition.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%22%7D"
                    src={partner.logo}
                    alt={partner.alt}
                    className="w-full h-full object-contain"
                  />
                </div>

                <div data-component-id="src/app/about/components/IndustryRecognition.tsx:232:16" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="232" data-component-end-line="242" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%22%7D" className="p-6">
                  <h3 data-component-id="src/app/about/components/IndustryRecognition.tsx:233:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="233" data-component-end-line="233" data-component-file="IndustryRecognition.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-bold%20text-text-primary%20mb-2%22%7D" className="text-lg font-bold text-text-primary mb-2">{partner.name}</h3>

                  <div data-component-id="src/app/about/components/IndustryRecognition.tsx:235:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="235" data-component-end-line="237" data-component-file="IndustryRecognition.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-block%20px-3%20py-1%20bg-primary%2F10%20text-primary%20text-xs%20font-semibold%20rounded-full%20mb-3%22%7D" className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-3">
                    {partner.role}
                  </div>

                  <p data-component-id="src/app/about/components/IndustryRecognition.tsx:239:18" data-component-path="src/app/about/components/IndustryRecognition.tsx" data-component-line="239" data-component-end-line="241" data-component-file="IndustryRecognition.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-sm%20leading-relaxed%22%7D" className="text-text-secondary text-sm leading-relaxed">
                    {partner.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default IndustryRecognition;